import React, { useCallback, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { addDoc, collection, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { FaThumbsDown, FaThumbsUp } from 'react-icons/fa';
import { IoMdArrowBack } from 'react-icons/io';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import DynamicTextArea from 'components/DynamicTextArea';
import { useUserInfo } from 'hooks/userinfo';
import { Review, ReviewDTO } from 'interfaces/Review';
import { db } from 'services/firebase';
import { trackGoogleAnalyticsEvent } from 'services/google-analytics';

import {
  Container,
  DiscardReviewButton,
  ReviewData,
  ReviewVeredictContainer,
  ReviewVeredict,
  SaveReviewButton,
} from './styles';

interface ReviewFormData {
  message: string;
}

interface WriteReviewProps {
  setReviewModeActive: React.Dispatch<React.SetStateAction<boolean>>;
  setReviews: React.Dispatch<React.SetStateAction<Review[]>>;
}

const WriteReview: React.FC<WriteReviewProps> = ({
  setReviewModeActive,
  setReviews,
}) => {
  const { serviceid } = useParams<{ serviceid: string }>();
  const { userInfo } = useUserInfo();

  const formRef = useRef<FormHandles>(null);

  const [reviewPositive, setReviewPositive] = useState(true);

  const handleSubmit = useCallback(
    async (data: ReviewFormData) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          message: Yup.string().required('Mensagem obrigatória'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        if (!userInfo) {
          toast.error(
            'Você precisa estar logado para poder fazer uma avaliação',
          );
          return;
        }

        const newReview: ReviewDTO = {
          author: {
            name: userInfo.displayName,
            id: userInfo.uid,
          },
          circle: {
            id: userInfo.circle.id,
            name: userInfo.circle.name,
          },
          message: data.message,
          positive: reviewPositive,
          serviceId: serviceid,
        };

        const reviewDocRef = await addDoc(
          collection(db, `services/${serviceid}/reviews`),
          newReview,
        );
        const reviewSnapshot = await getDoc(reviewDocRef);
        const review = {
          ...reviewSnapshot.data(),
          id: reviewSnapshot.id,
        } as Review;

        setReviews((reviews) => [...reviews, review]);

        toast.success('Avaliação feita com sucesso');

        // Analytics
        trackGoogleAnalyticsEvent(
          'review',
          `${reviewPositive ? 'positive' : 'negative'} review submitted`,
          review.message,
        );

        setReviewModeActive(false);
      } catch {
        toast.error(
          'Ocorreu um erro ao cadastrar a avaliação. Por favor tente novamente',
        );
      }
    },
    [reviewPositive, serviceid, setReviewModeActive, setReviews, userInfo],
  );

  return (
    <Container ref={formRef} onSubmit={handleSubmit}>
      <DiscardReviewButton onClick={() => setReviewModeActive(false)}>
        <IoMdArrowBack size={28} />
        <div style={{ width: 10 }} />
        <p>Voltar</p>
      </DiscardReviewButton>
      <div style={{ height: 20 }} />
      <ReviewData>
        <DynamicTextArea
          name="message"
          placeholder="Escreva aqui a sua avaliação deste serviço"
          style={{ minHeight: 115 }}
        />
        <div style={{ width: 10 }} />
        <ReviewVeredictContainer>
          <ReviewVeredict
            selected={reviewPositive}
            onClick={() => setReviewPositive(true)}
            type="button"
          >
            <FaThumbsUp size={40} color={reviewPositive ? 'green' : '#aaa'} />
          </ReviewVeredict>
          <div style={{ height: 15 }} />
          <ReviewVeredict
            selected={!reviewPositive}
            onClick={() => setReviewPositive(false)}
            type="button"
          >
            <FaThumbsDown size={40} color={!reviewPositive ? 'red' : '#aaa'} />
          </ReviewVeredict>
        </ReviewVeredictContainer>
      </ReviewData>
      <SaveReviewButton type="submit">Salvar Avaliação</SaveReviewButton>
    </Container>
  );
};

export default WriteReview;
